import React from 'react';

import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenTitle,
  useScreenDispatch,
  useScreenState,
} from '@gogame/screens';

import { AnnotationFormEdit } from 'scenes/Annotations/components/AnnotationForm';
import {
  COLUMN_PERIOD,
  COLUMN_TYPE,
  COLUMN_SUB_TYPE,
  COLUMN_CHANNELS,
  COLUMN_DESCRIPTION,
  COLUMN_APPLICATIONS,
  COLUMN_AFFILIATES,
  COLUMN_NETWORKS,
  COLUMN_CLUSTERS,
  COLUMN_PLATFORMS,
  COLUMN_USER,
  columnMenuConfig,
} from 'scenes/Annotations/constants';
import { useLoadAnnotationsTable } from 'scenes/Annotations/data';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/Annotations/screenReducer';

import AnnotationActions from '../Actions';
import Toolbar from './Toolbar';

const AnnotationsDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery, ready } = screenState;

  const { data, isLoading, isRefetching } = useLoadAnnotationsTable(
    {
      filters: filters,
      searchQuery: searchQuery,
      tableSettings: tableSettings,
    },
    {
      enabled: ready,
    }
  );
  const [childComponent, openChildComponent] = useChildComponent();

  const handleEditAction = ({ model }) => {
    openChildComponent(AnnotationFormEdit, {
      annotation: model,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        ready={ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        columnMenuConfig={columnMenuConfig}
        toolbar={<Toolbar />}
        storageKey={'annotations'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_PERIOD}
          type={'dateRange'}
          headerName={'Period'}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_DESCRIPTION}
          type={'text'}
          headerName={'Description'}
          width={352}
        />
        <DataGrid.Column
          field={COLUMN_SUB_TYPE}
          type={'text'}
          headerName={'Sub Type'}
          getValue={(v) => v?.name}
          width={150}
        />
        <DataGrid.Column
          field={COLUMN_CHANNELS}
          type={'commaList'}
          headerName={'Channels'}
        />
        <DataGrid.Column
          field={COLUMN_USER}
          type={'text'}
          headerName={'User'}
          getValue={(v) => v?.name}
          width={130}
        />
        <DataGrid.Column
          field={COLUMN_TYPE}
          type={'text'}
          headerName={'Type'}
          getValue={(v) => v?.name}
          width={110}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATIONS}
          type={'commaList'}
          headerName={'Applications'}
        />
        <DataGrid.Column
          field={COLUMN_NETWORKS}
          type={'commaList'}
          headerName={'Networks'}
        />
        <DataGrid.Column
          field={COLUMN_CLUSTERS}
          type={'commaList'}
          headerName={'Clusters'}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          type={'commaList'}
          headerName={'Platforms'}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATES}
          type={'commaList'}
          headerName={'Affiliates'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={AnnotationActions}
          limit={2}
          onEdit={handleEditAction}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default AnnotationsDataTable;
